import { useAuth } from '../store/auth';

const BASE = import.meta.env.VITE_API_URL;

interface TokenResponse {
  token: string;
  identity: string;
  ttl: number;
}

/**
 * Phase 3 baseline: fetch + decode a Twilio AccessToken JWT.
 * Phase 6 hardens this with retry, visibilitychange refresh, and
 * device.updateToken() integration so calls survive token expiry.
 */
export async function fetchTwilioToken(): Promise<TokenResponse & { expiresAt: number }> {
  const { idToken, sessionId } = useAuth.getState();
  if (!idToken) throw new Error('not authenticated');
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${idToken}`,
    'Content-Type': 'application/json',
  };
  // FR-66: every authed endpoint (including /token) requires the
  // X-Session-Id header. This module pre-dates the api/client wrapper
  // and does its own fetch — so it has to attach the header itself.
  if (sessionId) headers['X-Session-Id'] = sessionId;

  const res = await fetch(`${BASE}/token`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const body = await res.text();
    // Surface session_revoked to the global handler so the user bounces
    // to /login with the standard banner.
    if (res.status === 401 && body.includes('session_revoked')) {
      useAuth.getState().setSessionRevoked(true);
    }
    throw new Error(`token fetch failed (${res.status}): ${body}`);
  }
  const data = (await res.json()) as TokenResponse;
  const expiresAt = decodeJwtExp(data.token);
  return { ...data, expiresAt };
}

function decodeJwtExp(jwt: string): number {
  const parts = jwt.split('.');
  if (parts.length !== 3) return Date.now() + 4 * 60 * 60 * 1000;
  try {
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return payload.exp ? payload.exp * 1000 : Date.now() + 4 * 60 * 60 * 1000;
  } catch {
    return Date.now() + 4 * 60 * 60 * 1000;
  }
}
